import { AbsurdIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type PathLookupAdvancedOptions,
  type PathLookupAdvancedOptionsBase,
  type PathLookupIpProtocol,
  type PathLookupTcpFlag,
  factoryPathLookupAdvancedOptions,
  isPathLookupIcmpL4Options,
  isPathLookupTcpL4Options,
  isPathLookupUdpL4Options,
  pick,
  safeAbsurd,
} from '@ipfabric/utils';

export interface GraphAdvancedOptionsFormValues {
  applications: string;
  applicationsMatchWithRegex: boolean;
  category: string;
  dstPorts: string;
  dstRegions: string;
  enableRegions: boolean;
  flags: PathLookupTcpFlag[];
  fragmentOffset: number;
  protocol: PathLookupIpProtocol;
  srcPorts: string;
  srcRegions: string;
  tracked: boolean;
  ttl: number;
  icmpOptions: { type: number; code: number };
  url: string;
}

/**
 * Empty values to satisfy FormValues interface with fields not used for given options type
 *
 * These values should NEVER be used as initial form values in general, as they may differ
 * from factory advanced options defaults and lead to confusing "where did these defaults come from",
 * since we use first factoryPathLookupAdvancedOptions as default initial values,
 * see https://ipfabric.atlassian.net/browse/NIM-8219 for example problem.
 */
const advancedOptionsEmptyValues = {
  dstPorts: '0',
  flags: [],
  srcPorts: '0',
  icmpOptions: { type: 0, code: 0 },
};

export const advancedOptionsToFormValues = (
  advancedOptions: PathLookupAdvancedOptions,
): GraphAdvancedOptionsFormValues => {
  const values = {
    applications: advancedOptions.otherOptions.applications,
    applicationsMatchWithRegex:
      advancedOptions.otherOptions.applicationsMatchWithRegex ?? true,
    category: advancedOptions.otherOptions.category ?? '',
    dstRegions: advancedOptions.dstRegions,
    enableRegions: advancedOptions.enableRegions,
    fragmentOffset: advancedOptions.fragmentOffset,
    protocol: advancedOptions.protocol,
    srcRegions: advancedOptions.srcRegions,
    tracked: advancedOptions.otherOptions.tracked,
    ttl: advancedOptions.ttl,
    url: advancedOptions.otherOptions.url ?? '',
  };
  if (
    isPathLookupTcpL4Options(
      advancedOptions.protocol,
      advancedOptions.l4Options,
    )
  ) {
    return {
      ...values,
      dstPorts: advancedOptions.l4Options.dstPorts,
      flags: advancedOptions.l4Options.flags,
      srcPorts: advancedOptions.l4Options.srcPorts,
      ...pick(['icmpOptions'])(advancedOptionsEmptyValues),
    };
  }
  if (
    isPathLookupUdpL4Options(
      advancedOptions.protocol,
      advancedOptions.l4Options,
    )
  ) {
    return {
      ...values,
      dstPorts: advancedOptions.l4Options.dstPorts,
      srcPorts: advancedOptions.l4Options.srcPorts,
      ...pick(['flags', 'icmpOptions'])(advancedOptionsEmptyValues),
    };
  }
  if (
    isPathLookupIcmpL4Options(
      advancedOptions.protocol,
      advancedOptions.l4Options,
    )
  ) {
    return {
      ...values,
      icmpOptions: advancedOptions.l4Options,
      ...pick(['dstPorts', 'srcPorts', 'flags'])(advancedOptionsEmptyValues),
    };
  }
  safeAbsurd(advancedOptions.l4Options);
  return {
    ...values,
    ...pick(['dstPorts', 'srcPorts', 'flags', 'icmpOptions'])(
      advancedOptionsEmptyValues,
    ),
  };
};

export const formValuesToAdvancedOptions = (
  values: GraphAdvancedOptionsFormValues,
): PathLookupAdvancedOptions => {
  const commonValues: PathLookupAdvancedOptionsBase = {
    dstRegions: values.dstRegions,
    enableRegions: values.enableRegions,
    fragmentOffset: values.fragmentOffset,
    otherOptions: {
      applications: values.applications,
      applicationsMatchWithRegex: values.applicationsMatchWithRegex,
      category: values.category,
      tracked: values.tracked,
      url: values.url,
    },
    protocol: values.protocol,
    srcRegions: values.srcRegions,
    ttl: values.ttl,
  };

  switch (values.protocol) {
    case 'tcp':
      return {
        ...commonValues,
        l4Options: {
          dstPorts: values.dstPorts,
          srcPorts: values.srcPorts,
          flags: values.flags,
        },
      };
    case 'udp':
      return {
        ...commonValues,
        l4Options: {
          dstPorts: values.dstPorts,
          srcPorts: values.srcPorts,
        },
      };
    case 'icmp':
    case 'icmpv6':
      return {
        ...commonValues,
        l4Options: values.icmpOptions,
      };
    default:
      throw new AbsurdIpfError(values.protocol, 'Invalid protocol');
  }
};

// Fixed noUncheckedIndexedAccess NIM-19408
export const initialPathLookupAdvancedOptions =
  factoryPathLookupAdvancedOptions[0]!;

export const advancedOptionsInitialValues: GraphAdvancedOptionsFormValues =
  advancedOptionsToFormValues(initialPathLookupAdvancedOptions);
